import {
  Component,
  OnInit,
  OnChanges,
  Input,
  ViewChild,
  ViewChildren,
  QueryList,
  SimpleChanges
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { SelectItem, LazyLoadEvent } from 'primeng/api';
import { MultiSelect } from 'primeng/multiselect';
import { Table } from 'primeng/table';
import { Subscription, Observable } from 'rxjs';
import {
  EventSearchType,
  ProgramTypesGUID,
  EventStatus,
  EventStatusNames,
  ProgramTypes
} from '../../../models/_enum-collections';
import { CustomerPreviewInput } from '../../../models/customer-validation-preview-input.model';
import { MrppPreviewInput } from '../../../models/event-preview-input.model';
import { EventRegistrationModel } from '../../../models/event-registration.model';
import { EventSearchFilterRequest } from '../../../models/event-search-filter-request.model';
import { PagedResult } from '../../../models/page-result.model';
import { AppConstants } from '../../common/app-constants';
import { EditEventComponent } from '../../mcap-events/components/edit-event/edit-event.component';
import { LocalStorageService } from '../../services/local-storage-service';
import { OsmosisEditService } from '../../services/osmosis-edit.service';
import { CommonSharedModule } from '../../common/shared.module';
import { MrppPreviewComponent } from '../../mrpp/mrpp-preview/mrpp-preview.component';
import { EventDashboardService } from '../../services/event-dashboard.service';

@Component({
  selector: 'app-engagement-review-table',
  templateUrl: './engagement-review-table.component.html',
  styleUrls: ['./engagement-review-table.component.scss'],
  standalone: true,
  imports: [CommonSharedModule, EditEventComponent, MrppPreviewComponent]
})
export class EngagementReviewTableComponent implements OnInit, OnChanges {
  excelLoading = false;
  @Input() loading!: boolean;
  scrollableCols = new Array<any>();
  loadingIcon = 'fa-circle-o-notch';
  dataViewRecsPerPage!: number;
  pageLinksPerPage!: number;
  tblPageNumber = 1;
  totalRecords!: number;
  loadEditComponent!: boolean;
  editNav = false;
  eventIdPassToChild!: number;
  display!: boolean;
  showFullScreen!: boolean;
  canShowMrppPreviewModel!: boolean;
  @Input() eventSearchType!: EventSearchType;
  eventEditInput!: EventRegistrationModel;
  eventsDisplayed!: EventRegistrationModel[];
  allEventsDisplayed!: EventRegistrationModel[];
  eventSearchSubscription!: Subscription;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  delayedLoadSearchContent!: Function;
  eventSearchFilterRequest!: EventSearchFilterRequest;
  eventStatusDropdownItems: SelectItem[] = [];
  l1InternalStatusDropdownItems: SelectItem[] = [];
  l2InternalStatusDropdownItems: SelectItem[] = [];
  @ViewChild('dt', { static: false }) dt!: Table;
  @ViewChild('editNavChild', { static: false })
  editNavChild!: EditEventComponent;
  @ViewChildren('tak') components!: QueryList<MultiSelect>;
  customerValidationPreviewInput!: CustomerPreviewInput;
  customerSurveyPreviewInput!: CustomerPreviewInput;
  mrppPreviewInput!: MrppPreviewInput;
  canShowCustomerPreviewModel!: boolean;
  canShowCustomerValidationPreviewModel!: boolean;
  canShowCustomerSurveyPreviewModel!: boolean;
  mrppProgram: string = ProgramTypesGUID.MRPP;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private localStorage: LocalStorageService,
    private eventDashboardService: EventDashboardService,
    private osmService: OsmosisEditService
  ) {}

  ngOnInit() {
    this.loadEventStatusDropdownItems();
    this.getL1InternalStatusList();
    this.getL2InternalStatusList();
    this.delayedLoadSearchContent = _.debounce(this.loadEvents, 1000);

    this.dataViewRecsPerPage = AppConstants.dataListRecsPerPage;
    this.pageLinksPerPage = AppConstants.dataListPagerLinks;

    this.eventSearchFilterRequest = {
      pageSize: this.dataViewRecsPerPage,
      page: this.tblPageNumber
    };

    this.loadEvents({ first: 0 });

    this.scrollableCols = [
      { field: 'eventKey', header: 'Event Id' },
      { field: 'eventName', header: 'Workshop Type' },
      { field: 'partnerOrgName', header: 'Partner Org' },
      { field: 'fiscalYear', header: 'Fiscal Year' },
      { field: 'partnerMpnid', header: 'Partner MPN ID' },
      { field: 'l1InternalStatus', header: 'L1 Internal Status' },
      { field: 'l2InternalStatus', header: 'L2 Internal Status' },
      { field: 'poeDocUploadDate', header: 'Uploaded Date' },
      { field: 'submittedDate', header: 'Submitted Date' },
      { field: 'customerOrgName', header: 'Customer Org' },
      { field: 'customerCountry', header: 'Customer Country' }
    ];
    this.localStorage.setFullscreenMode(true);
    this.showFullScreen = this.localStorage.getFullscreenMode();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['eventSearchType'] && !changes['eventSearchType'].firstChange) {
      this.loadEvents({} as LazyLoadEvent);
    }
  }

  loadEventStatusDropdownItems(): void {
    const statusKeys = Object.keys(EventStatus).filter(
      x => !isNaN(parseInt(x, 0))
    );

    for (const key of statusKeys) {
      const itm: SelectItem = {
        label: EventStatusNames[EventStatus[parseInt(key, 0)]],
        value: EventStatusNames[EventStatus[parseInt(key, 0)]]
      };
      this.eventStatusDropdownItems.push(itm);
    }
  }

  getL1InternalStatusList() {
    this.osmService.getL1InternalStatusDropDown().subscribe(PTList => {
      for (const item of PTList) {
        const fl1InternalStatusDrpdwn: SelectItem = {
          label: item.l1internalStatus,
          value: item.l1internalStatus
        };
        this.l1InternalStatusDropdownItems.push(fl1InternalStatusDrpdwn);
      }
    });
  }

  getL2InternalStatusList() {
    this.osmService.getL2InternalStatusDropDown().subscribe(PTList => {
      for (const item of PTList) {
        const fl1InternalStatusDrpdwn: SelectItem = {
          label: item.l2internalStatus,
          value: item.l2internalStatus
        };
        this.l2InternalStatusDropdownItems.push(fl1InternalStatusDrpdwn);
      }
    });
  }

  loadEvents(event: LazyLoadEvent, refreshAfterPreview = false): void {
    if (!refreshAfterPreview) {
      if (event) {
        this.pageChange(event);
      } else {
        this.pageChange({ first: 0 });
      }
    }

    this.loading = true;
    if (this.eventSearchSubscription) {
      this.eventSearchSubscription.unsubscribe();
    }
    this.eventSearchFilterRequest.eventDocumentTypeId = this.eventSearchType;
    this.eventSearchFilterRequest.programTypeId = ProgramTypes.MRPP;
    this.eventSearchSubscription = this.eventDashboardService
      .getPOEDocReviewEngagements(this.eventSearchFilterRequest)
      .subscribe(data => {
        this.eventsDisplayed = data.results;
        this.totalRecords = data.rowCount;
        this.loading = false;
      });
  }

  export() {
    this.router.navigate(['download-csv'], { relativeTo: this.route });
  }

  pageChange(e) {
    const calc = e.first / this.dataViewRecsPerPage + 1;

    this.tblPageNumber = calc || 1;
    this.eventSearchFilterRequest.page = this.tblPageNumber;
  }

  onSearchInput(event, dt) {
    if (!event.target.value || event.target.value.trim() !== '') {
      dt.filterGlobal(event.target.value, 'contains');
    }
  }

  applyFilter(event) {
    this.totalRecords = event.filteredValue.length;
    this.pageChange({ first: 0 });
  }

  searchOrFilterEngagements() {
    this.eventSearchFilterRequest.page = 1;
    if (this.dt) {
      this.dt.first = this.eventSearchFilterRequest.page;
    }

    if (
      this.eventSearchFilterRequest.eventKey != undefined &&
      this.eventSearchFilterRequest.eventKey.toString() === ''
    ) {
      this.eventSearchFilterRequest.eventKey = undefined;
    }

    if (
      this.eventSearchFilterRequest.partnerOrgName != undefined &&
      this.eventSearchFilterRequest.partnerOrgName.toString() === ''
    ) {
      this.eventSearchFilterRequest.partnerOrgName = undefined;
    }

    if (
      this.eventSearchFilterRequest.partnerMpnid != undefined &&
      this.eventSearchFilterRequest.partnerMpnid.toString() === ''
    ) {
      this.eventSearchFilterRequest.partnerMpnid = undefined;
    }

    if (
      this.eventSearchFilterRequest.fiscalYear != undefined &&
      this.eventSearchFilterRequest.fiscalYear.toString() === ''
    ) {
      this.eventSearchFilterRequest.fiscalYear = undefined;
    }

    this.delayedLoadSearchContent();
  }

  get currentPageInfo(): string {
    if (this.totalRecords === 0) {
      return 'No Record Found';
    } else if (this.totalRecords > 0 && this.tblPageNumber === 1) {
      return `Showing 1 - ${this.eventsDisplayed.length} of ${this.totalRecords} Records`;
    } else if (this.totalRecords > 0 && this.tblPageNumber > 1) {
      const rangeStart =
        (this.tblPageNumber - 1) * this.dataViewRecsPerPage + 1;
      let rangeEnd = 0;

      if (this.tblPageNumber * this.dataViewRecsPerPage >= this.totalRecords) {
        rangeEnd = this.totalRecords;
      } else if (
        this.tblPageNumber * this.dataViewRecsPerPage <
        this.totalRecords
      ) {
        rangeEnd = this.tblPageNumber * this.dataViewRecsPerPage;
      }

      return `Showing ${rangeStart} - ${rangeEnd} of ${this.totalRecords} Records`;
    }
    return '';
  }

  reset() {
    this.components['_results'].forEach(ds => {
      ds.value = null;
    });

    if (this.dt) {
      this.dt.filter('', 'taskOwner', 'in');
      this.dt.filter('', 'l1InternalStatus', 'in');
      this.dt.filter('', 'l2InternalStatus', 'in');
      this.dt.filter('', 'engagementType', 'in');
      this.dt.reset();
    }

    this.eventSearchFilterRequest = {
      pageSize: this.dataViewRecsPerPage,
      page: this.tblPageNumber
    };

    this.display = false;
  }

  editRequestDetails(id: number) {
    this.loadEditComponent = true;
    this.editNav = true;
    this.eventIdPassToChild = id;
    this.eventEditInput = this.eventsDisplayed.filter(
      x => x.eventRegistrationId === id
    )[0];
  }

  previewFile(eventRegistrationId) {
    this.loading = false;

    this.mrppPreviewInput = {
      eventId: eventRegistrationId,
      previewFileIndex: 0,
      eventIdList: this.eventsDisplayed.map(event => event.eventRegistrationId),
      eventTypeId: this.eventsDisplayed.map(event => event.eventTypeId)[0],
      isOneToMany:
        this.eventsDisplayed.map(event => event.isOneToMany)[0] || false,
      eventCustomers: this.eventsDisplayed.map(item => ({
        eventId: item.eventRegistrationId,
        eventCustomers: item.eventCustomers || []
      }))
    } as any;

    if (this.eventSearchType === EventSearchType.InvoiceDocument) {
      this.mrppPreviewInput.isPoeInvoicePreview = true;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (
      this.eventSearchType === EventSearchType.DealAssociationRFPTemplate
    ) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = true;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (
      this.eventSearchType == EventSearchType.SKUPurchaseTrialActivationReview
    ) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = true;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (
      this.eventSearchType === EventSearchType.DealAssociationCPORTemplate
    ) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = true;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (
      this.eventSearchType === EventSearchType.DealAssociationPOETemplate
    ) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = true;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (this.eventSearchType === EventSearchType.MADPOE) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = true;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (this.eventSearchType === EventSearchType.PublicSectorPOE) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = true;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (
      this.eventSearchType === EventSearchType.MicrosoftInvoiceTemplate
    ) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = true;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (this.eventSearchType === EventSearchType.POETemplate) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = true;
      this.mrppPreviewInput.isSOWPreview = false;
    } else if (this.eventSearchType === EventSearchType.SOWDocument) {
      this.mrppPreviewInput.isPoeInvoicePreview = false;
      this.mrppPreviewInput.isSKUPurchaseTrialPreview = false;
      this.mrppPreviewInput.isRollCallPreview = false;
      this.mrppPreviewInput.isDealAssociationCPORTemplate = false;
      this.mrppPreviewInput.isDealAssociationPoeTemplate = false;
      this.mrppPreviewInput.isMADScreenshot = false;
      this.mrppPreviewInput.isPublicSectorTemplate = false;
      this.mrppPreviewInput.isMicrosoftInvoiceTemplate = false;
      this.mrppPreviewInput.isPOETemplatePreview = false;
      this.mrppPreviewInput.isSOWPreview = true;
    }
    this.canShowMrppPreviewModel = true;
  }

  closePreview() {
    this.canShowMrppPreviewModel = false;
  }

  onShowFullScreenChanged(showFullscreenValue: boolean) {
    this.showFullScreen = showFullscreenValue;
    this.localStorage.setFullscreenMode(this.showFullScreen);
  }

  refresh() {
    this.loadEvents({} as LazyLoadEvent, true);
  }

  sort(columnKey: string) {
    const column = this.scrollableCols.find(x => x.field === columnKey);

    column.descending =
      column.descending === undefined ? true : !column.descending;

    const columnIndex = this.scrollableCols.indexOf(column);

    this.scrollableCols[columnIndex] = column;

    this.eventSearchFilterRequest.sortField = column.field;
    this.eventSearchFilterRequest.sortOrderDescending = column.descending;
    this.loadEvents({} as LazyLoadEvent);
  }

  GetAllEventIds(): Observable<PagedResult<EventRegistrationModel>> {
    this.loading = true;

    const filter: EventSearchFilterRequest = {
      page: 1,
      pageSize: 100000,
      sortField: this.eventSearchFilterRequest.sortField,
      sortOrderDescending: this.eventSearchFilterRequest.sortOrderDescending
    };

    switch (this.eventSearchType) {
      case EventSearchType.AllEvents:
        return this.eventDashboardService.getAllEvents(filter);
      case EventSearchType.CustomerValidationReview:
        return this.eventDashboardService.getAwaitingCustomerValidationEvents(
          filter
        );
      case EventSearchType.InvoiceDocReview:
        return this.eventDashboardService.getAwaitingInvoiceDocReviewEvents(
          filter
        );
      case EventSearchType.CustomerSurveyReview:
        return this.eventDashboardService.getAwaitingCustomerSurveyReviewEvents(
          filter
        );
      case EventSearchType.SKUPurchaseTrialActivationReview:
        return this.eventDashboardService.getAwaitingSKUPurchaseReviewEvents(
          filter
        );
      default:
        return new Observable<PagedResult<EventRegistrationModel>>();
    }
  }

  showCustomerValidationPreview(eventRegistrationId) {
    this.GetAllEventIds().subscribe(data => {
      this.allEventsDisplayed = data.results;
      this.loading = false;
      this.canShowCustomerValidationPreviewModel = true;
      this.customerValidationPreviewInput = {
        eventId: eventRegistrationId,
        eventIdList: this.allEventsDisplayed.map(
          event => event.eventRegistrationId
        )
      };
    });
  }

  showCustomerSurveyPreview(eventRegistrationId) {
    this.GetAllEventIds().subscribe(data => {
      this.allEventsDisplayed = data.results;
      this.loading = false;
      this.canShowCustomerSurveyPreviewModel = true;
      this.customerSurveyPreviewInput = {
        eventId: eventRegistrationId,
        eventIdList: this.allEventsDisplayed.map(
          event => event.eventRegistrationId
        )
      };
    });
  }
  closeCustomerValidationPreview() {
    this.canShowCustomerValidationPreviewModel = false;
  }
  closeCustomerSurveyPreview() {
    this.canShowCustomerSurveyPreviewModel = false;
  }

  onCloseEditModal() {
    this.loadEditComponent = false;
    this.editNav = false;
    this.refresh();
    this.setEventStatus();
  }

  async setEventStatus() {
    const event = await this.loadEventInfo();
    if (event) {
      const eventIndex = this.eventsDisplayed.indexOf(
        this.eventsDisplayed.find(
          event =>
            event.eventRegistrationId ===
            this.eventEditInput.eventRegistrationId
        ) || ({} as EventRegistrationModel)
      );
      this.eventsDisplayed[eventIndex] = event;
    }
  }

  async loadEventInfo() {
    return await this.eventDashboardService
      .getEventRegistration(this.eventEditInput.eventRegistrationId)
      .toPromise();
  }

  getUploadedDate(rawData: EventRegistrationModel): string {
    if (
      rawData.eventRegistrationDocuments != null &&
      rawData.eventRegistrationDocuments.find(
        doc => doc.eventDocumentTypeId == this.eventSearchType
      ) != null
    ) {
      return (
        rawData.eventRegistrationDocuments.find(
          doc => doc.eventDocumentTypeId == this.eventSearchType
        )?.formattedUploadDate || ''
      );
    }
    return '';
  }

  getCustomerName(rawData: EventRegistrationModel): string {
    if (rawData.eventCustomers?.length != 0 && rawData.eventCustomers != null) {
      return rawData.eventCustomers[0].customerName;
    } else return '-';
  }

  getCustomerCountry(rawData: EventRegistrationModel): string {
    if (rawData.eventCustomers?.length != 0 && rawData.eventCustomers != null) {
      return rawData.eventCustomers[0].customerCountry;
    } else return '-';
  }
}
